import { useCallback, useEffect, useRef } from "react";
import { useToast } from "./use-toast";
import { walletErrorMessage } from "../wallet/wallet-errors";

/** Own wallet errors above the context-keyed UI, including session expiry. */
export function useWalletNotifications(contextKey: string, error: string | null) {
  const toast = useToast();
  const currentContext = useRef(contextKey);
  currentContext.current = contextKey;
  const active = useRef(true);
  const reported = useRef<{ id: string; message: string } | null>(null);

  const clear = useCallback(() => {
    if (reported.current) toast.dismiss(reported.current.id);
    reported.current = null;
  }, [toast]);
  const report = useCallback((message: string) => {
    // The controller state and the rejected action can describe the same error.
    if (reported.current?.message === message) return;
    clear();
    reported.current = { id: toast.error(message), message };
  }, [clear, toast]);

  useEffect(() => {
    active.current = true;
    return () => { active.current = false; clear(); };
  }, [clear]);

  useEffect(() => {
    if (error) report(error);
    else clear();
  }, [error, report, clear]);

  return (action: () => void | Promise<void>, fallback: string): void => {
    const startedIn = currentContext.current;
    clear();
    const onError = (failure: unknown) => {
      // State observation above handles current controller errors even when
      // connecting changes context; late failures must not notify a new context.
      if (!active.current || currentContext.current !== startedIn) return;
      report(walletErrorMessage(failure, fallback));
    };
    try { Promise.resolve(action()).catch(onError); }
    catch (failure) { onError(failure); }
  };
}
